import { Switch as KSwitch } from "@kobalte/core/switch";
import { StButton } from "@storytell/ui/buttons";
import { TbX } from "solid-icons/tb";
import { Show, createSignal, onCleanup, onMount } from "solid-js";
import type { AssetRecord } from "~/stores/sync/syncAssets";
import type { ConceptRecord } from "~/stores/sync/syncConcepts";
import { useWire } from "~/wire";
import { ScopeManagerPaneAssetsTable, useScopeManagerPaneAssetsData } from "./ScopeManagerPaneAssetsTable";
import { ScopeManagerPaneConceptsTable, useScopeManagerPaneConceptsData } from "./ScopeManagerPaneConceptsTable";
import { ScopeManagerPaneNoData } from "./ScopeManagerPaneNoData";

export const ScopeManagerPane = (props: { onClose?: () => void }) => {
  let ref!: HTMLDivElement;
  const [style, setStyle] = createSignal<{
    "max-width": string;
    "max-height": string;
  }>({
    "max-width": "100%",
    "max-height": "100%",
  });

  onMount(() => {
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        const wh = window.innerHeight;
        const ww = window.innerWidth;
        const { top, left } = ref.getBoundingClientRect();

        const maxWidth = ww - left;
        const maxHeight = wh - top;

        setStyle({
          "max-height": `${maxHeight - 10}px`,
          "max-width": `${maxWidth - 10}px`,
        });
      });
    });

    resizeObserver.observe(ref);
    resizeObserver.observe(document.body);
    onCleanup(() => {
      resizeObserver.disconnect();
    });
  });

  const [search, setSearch] = createSignal("");

  const { assets } = useScopeManagerPaneAssetsData(search);
  const { concepts } = useScopeManagerPaneConceptsData(search);

  return (
    <div class="w-full max-w-4xl p-2 flex flex-col" ref={ref} style={style()}>
      <Show
        when={assets().length > 0 || concepts().length > 0}
        fallback={<ScopeManagerPaneNoData onClose={props.onClose} />}
      >
        <ScopeManagerPaneWithData
          assets={assets()}
          concepts={concepts()}
          onClose={props.onClose}
          search={search()}
          setSearch={setSearch}
        />
      </Show>
    </div>
  );
};

const ScopeManagerPaneWithData = (props: {
  onClose?: () => void;
  assets: AssetRecord[];
  concepts: ConceptRecord[];
  search: string;
  setSearch: (search: string) => void;
}) => {
  const wire = useWire();
  let inputRef!: HTMLInputElement;
  onMount(() => {
    inputRef.focus();
  });
  return (
    <>
      <div class="flex items-center justify-between mb-3 gap-2">
        <input
          type="text"
          value={props.search}
          onInput={(e) => props.setSearch(e.currentTarget.value)}
          placeholder="Search all my Knowledge..."
          class="w-full px-3 py-2 border border-primary-border rounded bg-tile text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          aria-label="Search assets"
          ref={inputRef}
        />
        <StButton
          icon={TbX}
          aria-label="Close"
          class="ml-2 shrink-0"
          noColor
          noBorder
          onClick={() => props.onClose?.()}
        />
      </div>

      <div class="mb-3 flex items-center gap-2 shrink-0">
        <KSwitch
          class="flex items-center gap-2 cursor-pointer"
          onChange={(checked) => {
            wire.chat.scope.setAllKnowledge(checked);
          }}
          checked={wire.chat.scope.allKnowledge()}
        >
          <KSwitch.Input class="peer" />
          <KSwitch.Control class="inline-flex items-center h-6 w-11 pl-0.5 rounded-full bg-inset transition-colors duration-250 data-[checked]:bg-brand peer-focus:ring-2 peer-focus:ring-brand-hover">
            <KSwitch.Thumb class="h-5 w-5 rounded-full bg-tile transition-transform duration-250 data-[checked]:translate-x-[100%]" />
          </KSwitch.Control>
          <KSwitch.Label class="flex items-center text-2xs select-none cursor-pointer gap-1">
            Include all knowledge
          </KSwitch.Label>
        </KSwitch>
      </div>

      <ScopeManagerPaneConceptsTable concepts={props.concepts} />
      <ScopeManagerPaneAssetsTable assets={props.assets} />

      <p class="text-secondary-text text-xs mb-2">
        Storytell will only use public knowledge to answer if you don't specify a scope.{" "}
        <a href="https://go.storytell.ai/learn-scope" target="_blank" rel="noreferrer" class="text-brand underline">
          Learn more
        </a>
      </p>
    </>
  );
};
